import axiosInstance from "./axiosInstance";
import { LoginRequest, LoginResponse } from "types/auth";
import { User } from "common/models/types";
import { log, LogLevel } from "utils/LogUtil";

// 로그인 API 호출
export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  const response = await axiosInstance.post<LoginResponse>("/api/v1/auth/login", credentials);
  return response.data;
};

// 로그아웃 API 호출
export const logout = async (): Promise<void> => {
  await axiosInstance.post("/api/v1/auth/logout");
};

// 토큰 갱신 API 호출
export const refreshToken = async (): Promise<void> => {
  await axiosInstance.post("/api/v1/auth/refresh");
};

// 현재 사용자 정보 조회 API 호출
export const getCurrentUser = async (): Promise<User> => {
  try {
    const response = await axiosInstance.get<{ data: User; error?: any }>("/api/v1/users/me");
    log(LogLevel.VERBOSE, "getCurrentUser 응답 전체:", response);

    // 응답에 error 필드가 있는지 확인
    if (
      response.data &&
      typeof response.data === "object" &&
      "error" in response.data &&
      response.data.error
    ) {
      log(LogLevel.ERROR, "API 응답에 error 포함:", response.data.error);
      throw new Error(
        typeof response.data.error === "string"
          ? response.data.error
          : "사용자 정보를 가져오는 중 오류가 발생했습니다."
      );
    }

    let userData: User;

    // 응답 형식 확인 - data.data가 존재하는 경우 (중첩된 구조)
    if (response.data && typeof response.data === "object" && "data" in response.data) {
      userData = response.data.data;
      log(LogLevel.VERBOSE, "응답에서 data.data를 사용:", userData);
    }
    // data 자체가 User 객체인 경우
    else if (response.data && typeof response.data === "object" && "id" in response.data) {
      userData = response.data as unknown as User;
      log(LogLevel.VERBOSE, "응답에서 data를 사용:", userData);
    }
    // 아무것도 해당하지 않는 경우 기본 User 객체 생성 (임시)
    else {
      console.warn("예상치 못한 응답 형식:", response.data);
      userData = {
        id: "temp",
        email: "temp@example.com",
        name: "임시 사용자",
        roles: ["user"], // 기본 사용자 권한 부여
      };
    }

    // roles 확인 및 기본값 설정
    if (!userData.roles || !Array.isArray(userData.roles)) {
      console.warn("roles 필드가 없거나 배열이 아님:", userData);
      userData.roles = ["user"]; // 기본 사용자 권한 부여
    }

    return userData;
  } catch (error) {
    log(LogLevel.ERROR, "getCurrentUser 오류:", error);
    throw error;
  }
};

// 비밀번호 변경 API 호출
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  try {
    const response = await axiosInstance.post("/api/v1/auth/change-password", {
      currentPassword,
      newPassword,
    });

    if (response.data && response.data.error) {
      throw new Error(response.data.error);
    }

    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "changePassword 오류:", error);
    throw error;
  }
};

// 비밀번호 변경 API 호출
export const adminChangePassword = async (
  targetUserId: string,
  newPassword: string
): Promise<void> => {
  try {
    const response = await axiosInstance.post("/api/v1/auth/admin/change-password", {
      targetUserId,
      newPassword,
    });

    if (response.data && response.data.error) {
      throw new Error(response.data.error);
    }

    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "changePassword 오류:", error);
    throw error;
  }
};
